import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { adminGetFamilyDetail, adminDownloadFamilyReport, formatCurrency } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Users, Lock, ShieldCheck, TrendingUp, TrendingDown,
  PiggyBank, CreditCard, AlertTriangle, CheckCircle2, Activity, Calendar,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const TYPE_INFO = {
  REDDITO:  { label: 'Reddito',   color: '#16A34A', tw: 'text-emerald-600 dark:text-emerald-400' },
  SPESE:    { label: 'Spese',     color: '#DC2626', tw: 'text-red-500' },
  RISPARMI: { label: 'Risparmi',  color: '#2563EB', tw: 'text-blue-500' },
  DEBITI:   { label: 'Debiti',    color: '#EA580C', tw: 'text-orange-500' },
};

const Stat = ({ label, value, accent = 'text-primary', icon: Icon, hint }) => (
  <Card>
    <CardContent className="pt-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</p>
          {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Icon className={`w-5 h-5 ${accent}`} strokeWidth={1.5} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const AdminFamilyDetailPage = () => {
  const { user } = useAuth();
  const { familyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminGetFamilyDetail(familyId);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Solo admin'); navigate('/');
      } else if (e.response?.status === 404) {
        toast.error('Famiglia non trovata'); navigate('/admin/families');
      } else {
        toast.error('Errore caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [familyId]);

  const downloadReport = async (year) => {
    setDownloading(true);
    try {
      const blob = await adminDownloadFamilyReport(familyId, year);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-report-${data.family.code}-${year || new Date().getFullYear()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report PDF pronto');
    } catch {
      toast.error('Errore generazione PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Lock className="w-12 h-12 text-muted-foreground mb-3" strokeWidth={1.5} />
        <h2 className="text-xl font-semibold mb-1">Area riservata</h2>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 bg-muted rounded-xl" />)}
        </div>
        <div className="h-72 bg-muted rounded-xl" />
      </div>
    );
  }

  const { family, members, monthly_series_12m, top_categories_lifetime, totals_12m, indicators, counts } = data;
  const todayYear = new Date().getFullYear();

  // Health indicator (basato su indicators.savings_rate_avg, expense_rate_avg, months_with_negative)
  let healthLabel = 'Buona';
  let healthColor = 'emerald';
  if (indicators.months_with_negative_balance >= 6) { healthLabel = 'Critica'; healthColor = 'red'; }
  else if (indicators.savings_rate_avg_pct < 5 || indicators.expense_rate_avg_pct > 90) { healthLabel = 'Da migliorare'; healthColor = 'amber'; }
  else if (indicators.savings_rate_avg_pct >= 15) { healthLabel = 'Eccellente'; healthColor = 'emerald'; }

  return (
    <div className="space-y-6 animate-in" data-testid="admin-family-detail">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/families')} className="mb-2 -ml-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> Tutte le famiglie
        </Button>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <Badge className="bg-primary/10 text-primary border-0">ADMIN</Badge>
            </div>
            <h1 className="text-3xl font-bold tracking-tight font-mono">{family.code}</h1>
            <p className="text-muted-foreground">
              {family.name || `Famiglia ${family.code}`} · creata{' '}
              {family.created_at ? new Date(family.created_at).toLocaleDateString('it-IT') : '—'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadReport(todayYear)} disabled={downloading} data-testid="download-report-btn">
              <FileText className="w-4 h-4 mr-2" /> Report {todayYear}
            </Button>
            <Button variant="outline" onClick={() => downloadReport(todayYear - 1)} disabled={downloading}>
              <FileText className="w-4 h-4 mr-2" /> Report {todayYear - 1}
            </Button>
          </div>
        </div>
      </div>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" /> Membri ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-xs font-semibold text-primary">
                    {m.name.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{m.name}</p>
                    {m.role === 'admin' && <Badge className="bg-primary/10 text-primary border-0 text-[9px]">admin</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                <p className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {m.created_at ? new Date(m.created_at).toLocaleDateString('it-IT') : ''}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Indicators */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Saldo medio mens." value={formatCurrency(indicators.avg_monthly_balance)}
              accent={indicators.avg_monthly_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}
              icon={indicators.avg_monthly_balance >= 0 ? CheckCircle2 : AlertTriangle}
              hint="ultimi 12 mesi" />
        <Stat label="Mesi in negativo" value={`${indicators.months_with_negative_balance} / 12`}
              accent={indicators.months_with_negative_balance > 3 ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}
              icon={Calendar} />
        <Stat label="Risparmio medio" value={`${indicators.savings_rate_avg_pct}%`}
              accent={indicators.savings_rate_avg_pct >= 15 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600'}
              icon={PiggyBank} hint="del reddito 12m" />
        <Stat label="Salute" value={healthLabel}
              accent={healthColor === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : healthColor === 'red' ? 'text-red-500' : 'text-amber-600'}
              icon={Activity} />
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Transazioni" value={counts.transactions} />
        <Stat label="Categorie" value={counts.categories} />
        <Stat label="Budget" value={counts.budgets} accent="text-blue-500" />
        <Stat label="Obiettivi" value={counts.goals} accent="text-orange-500" />
        <Stat label="Ricorrenti" value={counts.recurring_bills} accent="text-purple-500" />
      </div>

      {/* Andamento 12 mesi */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Andamento ultimi 12 mesi</CardTitle></CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly_series_12m}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v) => formatCurrency(v)}
                  contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                />
                <Area type="monotone" dataKey="REDDITO" stackId="1" stroke="#16A34A" fill="#16A34A" fillOpacity={0.18} />
                <Area type="monotone" dataKey="SPESE" stackId="2" stroke="#DC2626" fill="#DC2626" fillOpacity={0.18} />
                <Area type="monotone" dataKey="RISPARMI" stackId="2" stroke="#2563EB" fill="#2563EB" fillOpacity={0.18} />
                <Area type="monotone" dataKey="DEBITI" stackId="2" stroke="#EA580C" fill="#EA580C" fillOpacity={0.18} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Saldo netto bar */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Saldo netto mensile</CardTitle></CardHeader>
        <CardContent>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly_series_12m}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="balance" radius={[4, 4, 0, 0]}>
                  {monthly_series_12m.map((m, i) => (
                    <Bar key={i} fill={m.balance >= 0 ? '#16A34A' : '#DC2626'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Totali 12 mesi */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.keys(TYPE_INFO).map((t) => (
          <Stat key={t}
                label={`${TYPE_INFO[t].label} 12m`}
                value={formatCurrency(totals_12m[t] || 0)}
                accent={TYPE_INFO[t].tw}
                icon={t === 'REDDITO' ? TrendingUp : t === 'SPESE' ? TrendingDown : t === 'RISPARMI' ? PiggyBank : CreditCard} />
        ))}
      </div>

      {/* Top categorie lifetime */}
      {top_categories_lifetime.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg">Top 10 categorie (lifetime)</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_categories_lifetime.map((c, i) => {
                const max = top_categories_lifetime[0].amount;
                const pct = (c.amount / max) * 100;
                const tw = TYPE_INFO[c.type]?.tw || 'text-foreground';
                const color = TYPE_INFO[c.type]?.color || '#666';
                return (
                  <div key={c.category_id} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium truncate text-sm">{c.name}</p>
                        <p className={`tabular-nums text-sm font-semibold ${tw}`}>{formatCurrency(c.amount)}</p>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full transition-all" style={{ background: color, width: `${pct}%` }} />
                      </div>
                      <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                        {c.count} transazioni · {TYPE_INFO[c.type]?.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminFamilyDetailPage;
